import { useEffect, useMemo } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

type VehicleType = 'MOTORCYCLE' | 'CAR' | 'TRUCK' | 'OTHER';

interface Device {
  id: string;
  imei: string;
  name: string | null;
  isOffline?: boolean;
  isOverdue?: boolean;
  isInRepo?: boolean;
  isAggressive?: boolean;
  vehicleType?: VehicleType | null;
  vehiclePlate?: string | null;
  lastPosition: {
    latitude: number;
    longitude: number;
    speed: number | null;
    deviceTime: string;
  } | null;
}

interface DeviceClusterProps {
  devices: Device[];
  onDeviceClick?: (deviceId: string) => void;
}

// SVG icons for vehicle types
const vehicleIcons: Record<VehicleType | 'DEFAULT', string> = {
  MOTORCYCLE: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="0.5">
    <circle cx="5" cy="17" r="2.5" fill="none" stroke="white" stroke-width="1.5"/>
    <circle cx="19" cy="17" r="2.5" fill="none" stroke="white" stroke-width="1.5"/>
    <path d="M5 17h3l3-5h4l2.5 5H19" fill="none" stroke="white" stroke-width="1.5"/>
    <path d="M11 12V9h3l1.5 3" fill="none" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="7" r="1.5" fill="white"/>
  </svg>`,
  CAR: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white">
    <path d="M5 16c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1H5v1zm12 0c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h-3v1z"/>
    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 11v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-4.99zM6.5 15c-.83 0-1.5-.67-1.5-1.5S5.67 12 6.5 12s1.5.67 1.5 1.5S7.33 15 6.5 15zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 10l1.5-4.5h11L19 10H5z"/>
  </svg>`,
  TRUCK: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white">
    <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
  </svg>`,
  OTHER: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white">
    <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
  </svg>`,
  DEFAULT: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white">
    <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
  </svg>`,
};

// Create custom marker icons
function createMarkerIcon(device: Device): L.DivIcon {
  let bgColor = '#22c55e'; // green - online
  let borderColor = '#16a34a';

  if (device.isOffline) {
    bgColor = '#9ca3af'; // gray - offline
    borderColor = '#6b7280';
  } else if (device.isAggressive) {
    bgColor = '#dc2626'; // red - aggressive
    borderColor = '#b91c1c';
  } else if (device.isInRepo) {
    bgColor = '#f97316'; // orange - repo
    borderColor = '#ea580c';
  } else if (device.isOverdue) {
    bgColor = '#eab308'; // yellow - overdue
    borderColor = '#ca8a04';
  }

  const speed = device.lastPosition?.speed || 0;
  const isMoving = speed > 5;
  const vehicleIcon = vehicleIcons[device.vehicleType || 'DEFAULT'];

  return new L.DivIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${bgColor};
        border: 2px solid ${borderColor};
        border-radius: 50%;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ${isMoving ? 'animation: pulse 1.5s infinite;' : ''}
      ">
        ${vehicleIcon}
      </div>
      <style>
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      </style>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
}

// Create cluster icon
function createClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const count = cluster.getChildCount();
  let size = 40;
  let bgColor = '#3b82f6';

  if (count > 50) {
    size = 60;
    bgColor = '#1d4ed8';
  } else if (count > 20) {
    size = 50;
    bgColor = '#2563eb';
  }

  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${bgColor};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${size > 50 ? '16px' : '14px'};
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        border: 3px solid white;
      ">
        ${count}
      </div>
    `,
    className: 'marker-cluster',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export function DeviceCluster({ devices, onDeviceClick }: DeviceClusterProps) {
  const map = useMap();

  // Create cluster group
  const clusterGroup = useMemo(() => {
    const group = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: createClusterIcon,
      animate: true,
    });

    return group;
  }, []);

  useEffect(() => {
    // Clear existing markers
    clusterGroup.clearLayers();

    // Add markers for devices with positions
    devices
      .filter((device) => device.lastPosition)
      .forEach((device) => {
        const marker = L.marker(
          [device.lastPosition!.latitude, device.lastPosition!.longitude],
          { icon: createMarkerIcon(device) }
        );

        // Create popup content
        const speed = device.lastPosition?.speed || 0;
        const vehicleTypeLabels: Record<string, string> = {
          MOTORCYCLE: 'Motocicleta',
          CAR: 'Automovil',
          TRUCK: 'Camión',
          OTHER: 'Otro',
        };
        const vehicleLabel = device.vehicleType ? vehicleTypeLabels[device.vehicleType] || device.vehicleType : '';
        const popupContent = `
          <div class="text-sm min-w-48">
            <p class="font-semibold text-gray-900">${device.name || device.imei}</p>
            ${device.vehiclePlate ? `<p class="text-gray-700 font-medium">Placa: ${device.vehiclePlate}</p>` : ''}
            ${vehicleLabel ? `<p class="text-gray-600">Tipo: ${vehicleLabel}</p>` : ''}
            <p class="text-gray-600">IMEI: ${device.imei}</p>
            <p class="text-gray-600">Velocidad: ${speed.toFixed(0)} km/h</p>
            <p class="text-xs text-gray-400 mt-1">
              ${new Date(device.lastPosition!.deviceTime).toLocaleString()}
            </p>
            ${device.isOverdue ? '<span class="inline-block mt-1 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">Atrasado</span>' : ''}
            ${device.isInRepo ? '<span class="inline-block mt-1 ml-1 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">Incaute</span>' : ''}
            ${device.isAggressive ? '<span class="inline-block mt-1 ml-1 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">Agresivo</span>' : ''}
          </div>
        `;

        marker.bindPopup(popupContent);

        // Handle click
        marker.on('click', () => {
          if (onDeviceClick) {
            onDeviceClick(device.id);
          }
        });

        clusterGroup.addLayer(marker);
      });

    // Add cluster group to map
    map.addLayer(clusterGroup);

    return () => {
      map.removeLayer(clusterGroup);
    };
  }, [devices, clusterGroup, map, onDeviceClick]);

  return null;
}
